import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CertificateCard } from "@/components/CertificateCard";
import { Certificate as CertIcon, Printer, ArrowLeft, BookOpen } from "@phosphor-icons/react";

export default function Certificate() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [pkg, setPkg] = useState(null);
  const [done, setDone] = useState(false);
  const [nextPkg, setNextPkg] = useState(null);

  const switchLearner = async (n) => {
    if (n === user?.student_name) return;
    try { await api.post("/profile/active-learner", { name: n }); await checkAuth(); } catch {}
  };

  useEffect(() => {
    api.get(`/library/${slug}`).then((r) => {
      setPkg(r.data);
      const total = (r.data.lessons || []).length;
      const completed = (r.data.completed_lessons || []).length;
      const finished = total > 0 && completed >= total;
      setDone(finished);
      if (finished) api.get(`/packages/${slug}/next`).then((n) => setNextPkg(n.data.next)).catch(() => {});
    }).catch(() => navigate("/library"));
  }, [slug, navigate]);

  if (!pkg) return <div className="min-h-[60vh] grid place-items-center font-body text-ink-soft">Loading…</div>;

  const today = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const color = pkg.color || "#0ea5e9";

  if (!done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="certificate-locked">
        <div className="grid place-items-center w-20 h-20 mx-auto rounded-3xl bg-brand-sun/40 mb-6">
          <CertIcon size={40} weight="duotone" className="text-ink" />
        </div>
        <h1 className="font-heading font-bold text-3xl text-ink">Almost there!</h1>
        <p className="font-body text-ink-soft mt-3">Finish all the lessons in <span className="font-semibold">{pkg.title}</span> to earn your certificate.</p>
        <Link to={`/read/${pkg.slug}`}><Button className="mt-6 rounded-full text-white font-bold px-8 py-6" style={{ backgroundColor: color }}>
          <BookOpen size={20} weight="duotone" className="mr-2" /> Keep reading
        </Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="certificate-page">
      <div className="flex items-center justify-between mb-6 no-print">
        <Link to="/library" className="inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky">
          <ArrowLeft size={18} weight="bold" /> Back to bookshelf
        </Link>
        <Button onClick={() => window.print()} className="rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-bold px-6" data-testid="print-certificate-btn">
          <Printer size={20} weight="duotone" className="mr-2" /> Print / Save PDF
        </Button>
      </div>

      {(user?.learners?.length || 0) > 1 && (
        <div className="no-print mb-5 flex flex-wrap items-center gap-2" data-testid="cert-learner-switcher">
          <span className="font-body text-sm font-semibold text-ink-soft mr-1">Print this certificate for:</span>
          {user.learners.map((l) => (
            <button
              key={l}
              onClick={() => switchLearner(l)}
              className={`px-4 py-1.5 rounded-full font-body font-semibold text-sm border-2 transition-colors ${
                l === user.student_name ? "bg-brand-sky text-white border-brand-sky" : "bg-white text-ink border-slate-900/10 hover:border-brand-sky"}`}
              data-testid={`cert-learner-${l}`}
            >
              {l}
            </button>
          ))}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
      >
        <CertificateCard name={user?.student_name || user?.name} packageTitle={pkg.title} level={pkg.level} color={color} />
      </motion.div>

      {nextPkg && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="no-print mt-8 bg-white sticker rounded-3xl p-6 flex flex-col sm:flex-row items-center gap-5" data-testid="cert-next-nudge">
          <img src={nextPkg.image} alt={nextPkg.title} className="w-20 h-20 rounded-2xl object-cover shrink-0" />
          <div className="flex-1 text-center sm:text-left">
            <p className="font-body text-xs uppercase tracking-widest" style={{ color: nextPkg.color }}>Recommended next level</p>
            <p className="font-heading font-bold text-xl text-ink">{nextPkg.title}</p>
            <p className="font-body text-sm text-ink-soft">{nextPkg.level} · ready for the next challenge?</p>
          </div>
          <Link to={`/catalog/${nextPkg.slug}`} data-testid="cert-next-btn">
            <Button className="rounded-full text-white font-bold px-7 py-5" style={{ backgroundColor: nextPkg.color }}>
              Explore next level
            </Button>
          </Link>
        </motion.div>
      )}
    </div>
  );
}
